import React, { useEffect, useState } from 'react';
import {
  Box,
  CircularProgress,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import {
  scottishSchoolService,
  type ScottishSchoolWithCouncil,
} from '../../services/scottishSchoolService';

const SCHOOL_NOT_LISTED = '__NOT_LISTED__';

interface Props {
  /** Called when the parent picks a real school — council is auto-corrected from the school's councilId+councilName. */
  onSchoolSelected: (school: ScottishSchoolWithCouncil) => void;
  /** Called when the parent chooses "My school isn't in this list" — free-text path. */
  onSchoolNotFound: (councilName: string, schoolType: string, schoolName: string) => void;
}

const UnknownCouncilSchoolPicker: React.FC<Props> = ({ onSchoolSelected, onSchoolNotFound }) => {
  const [councilName, setCouncilName] = useState('');
  const [schoolType, setSchoolType] = useState('');
  const [schools, setSchools] = useState<ScottishSchoolWithCouncil[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedSchoolId, setSelectedSchoolId] = useState<string>('');
  const [freeTextSchool, setFreeTextSchool] = useState('');
  const [showFreeText, setShowFreeText] = useState(false);

  useEffect(() => {
    if (!schoolType) return;
    setSchools([]);
    setSelectedSchoolId('');
    setShowFreeText(false);
    setLoading(true);
    scottishSchoolService
      .getAllSchools(schoolType)
      .then(setSchools)
      .catch(() => setSchools([]))
      .finally(() => setLoading(false));
  }, [schoolType]);

  const handleSchoolChange = (schoolIdStr: string) => {
    setSelectedSchoolId(schoolIdStr);
    if (schoolIdStr === SCHOOL_NOT_LISTED) {
      setShowFreeText(true);
    } else {
      setShowFreeText(false);
      const school = schools.find((s) => String(s.id) === schoolIdStr);
      if (school) onSchoolSelected(school);
    }
  };

  const handleFreeTextConfirm = () => {
    if (freeTextSchool.trim()) {
      onSchoolNotFound(councilName, schoolType, freeTextSchool.trim());
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="body2" color="text.secondary">
        Enter your council name and select a school type to search all Scottish schools.
      </Typography>

      <TextField
        label="Council name (approximate)"
        value={councilName}
        onChange={(e) => setCouncilName(e.target.value)}
        required
        fullWidth
        helperText="Type the name of your council as best you can"
      />

      <FormControl fullWidth required>
        <InputLabel>School type</InputLabel>
        <Select
          value={schoolType}
          label="School type"
          onChange={(e) => setSchoolType(e.target.value)}
        >
          <MenuItem value="PRIMARY">Primary</MenuItem>
          <MenuItem value="SECONDARY">Secondary</MenuItem>
        </Select>
        <FormHelperText>Select school type to load matching schools</FormHelperText>
      </FormControl>

      {loading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircularProgress size={18} />
          <Typography variant="body2">Loading schools…</Typography>
        </Box>
      )}

      {!loading && schools.length > 0 && (
        <FormControl fullWidth required>
          <InputLabel>Select your school</InputLabel>
          <Select
            value={selectedSchoolId}
            label="Select your school"
            onChange={(e) => handleSchoolChange(e.target.value)}
          >
            {schools.map((s) => (
              <MenuItem key={s.id} value={String(s.id)}>
                {s.name} <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>({s.councilName})</Typography>
              </MenuItem>
            ))}
            <MenuItem value={SCHOOL_NOT_LISTED} sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
              My school isn't in this list
            </MenuItem>
          </Select>
        </FormControl>
      )}

      {showFreeText && (
        <TextField
          label="School name"
          value={freeTextSchool}
          onChange={(e) => {
            setFreeTextSchool(e.target.value);
            if (e.target.value.trim()) handleFreeTextConfirm();
          }}
          required
          fullWidth
          helperText="Enter your school name as accurately as possible — an admin will confirm the details"
        />
      )}
    </Box>
  );
};

export default UnknownCouncilSchoolPicker;
